import * as dcmjs from 'dcmjs';
import { StorageSOPClassUID } from './modality';

/**
 * Reads DICOM headers of files chosen for import without uploading them; parsing stops at PixelData.
 * The SOP class is read from the dataset (0008,0016), never the synthesizable file-meta group.
 */

const PIXEL_DATA_TAG = '7FE00010';

/** SOP classes the connector stores as slices of a 3D image series. */
const IMAGE_SOP_CLASS_UIDS: ReadonlySet<string> = new Set([
    StorageSOPClassUID.CTImageStorage,
    StorageSOPClassUID.EnhancedCTImageStorage,
    StorageSOPClassUID.MRImageStorage,
    StorageSOPClassUID.EnhancedMRImageStorage,
    StorageSOPClassUID.PETImageStorage,
]);

/** SOP classes the connector stores as one standalone dataset each. */
const SINGLE_INSTANCE_SOP_CLASS_UIDS: ReadonlySet<string> = new Set([
    StorageSOPClassUID.SpatialRegistrationStorage,
    StorageSOPClassUID.DeformableSpatialRegistrationStorage,
    StorageSOPClassUID.RTDoseStorage,
    StorageSOPClassUID.RTStructureSetStorage,
    StorageSOPClassUID.RTPlanStorage,
    StorageSOPClassUID.RTIonPlanStorage,
]);

/** SOP classes the connector accepts; anything else is rejected as unsupported. */
const ACCEPTED_SOP_CLASS_UIDS: ReadonlySet<string> = new Set([
    ...IMAGE_SOP_CLASS_UIDS,
    ...SINGLE_INSTANCE_SOP_CLASS_UIDS,
]);

export function isImageSopClass(sopClassUid: string): boolean {
    return IMAGE_SOP_CLASS_UIDS.has(sopClassUid);
}

export type ScannedInstance = {
    file: File;
    dicomPatientId: string;
    dicomPatientName?: string;
    patientSex?: string;
    patientBirthDate?: string;
    studyInstanceUid: string;
    seriesInstanceUid: string;
    /** Always non-empty, and unique among the whole scan's instances. */
    sopInstanceUid: string;
    sopClassUid: string;
    modality?: string;
    seriesDescription?: string;
    seriesDateTime?: string;
};

export type ScanRejectReason =
    | 'unreadable'
    | 'not-dicom'
    | 'unsupported-sop-class'
    | 'missing-patient-id'
    | 'missing-study-uid'
    | 'missing-series-uid'
    | 'missing-sop-uid'
    | 'duplicate-sop-uid';

export type ScanReject = {
    fileName: string;
    reason: ScanRejectReason;
};

export type ScanResult = {
    instances: ScannedInstance[];
    rejects: ScanReject[];
};

function firstString(value: unknown): string | undefined {
    if (typeof value === 'string') {
        return value.trim() || undefined;
    }
    if (Array.isArray(value)) {
        return firstString(value[0]);
    }
    // dcmjs renders PersonName values as { Alphabetic: 'SMITH^JOHN' }.
    if (value && typeof value === 'object' && 'Alphabetic' in value) {
        return firstString((value as { Alphabetic: unknown }).Alphabetic);
    }
    return undefined;
}

/** DICOM DA `YYYYMMDD` to ISO `YYYY-MM-DD`; undefined when the date is absent or too short. */
export function dicomDateToIso(date: string | undefined): string | undefined {
    if (!date || date.length < 8) {
        return undefined;
    }
    return `${date.slice(0, 4)}-${date.slice(4, 6)}-${date.slice(6, 8)}`;
}

/** DICOM DA `YYYYMMDD` + TM `HHMMSS` to an ISO instant; undefined when the date is absent. */
function toIsoDateTime(
    date: string | undefined,
    time: string | undefined
): string | undefined {
    const isoDate = dicomDateToIso(date);
    if (isoDate === undefined) {
        return undefined;
    }
    const hh = time?.slice(0, 2) || '00';
    const mm = time?.slice(2, 4) || '00';
    const ss = time?.slice(4, 6) || '00';
    return `${isoDate}T${hh}:${mm}:${ss}`;
}

function scanBuffer(
    buffer: ArrayBuffer,
    file: File
): ScannedInstance | ScanReject {
    let dataset: Record<string, unknown>;
    try {
        const parsed = dcmjs.data.DicomMessage.readFile(buffer, {
            untilTag: PIXEL_DATA_TAG,
        });
        dataset = dcmjs.data.DicomMetaDictionary.naturalizeDataset(
            parsed.dict
        ) as Record<string, unknown>;
    } catch {
        return { fileName: file.name, reason: 'not-dicom' };
    }

    const sopClassUid = firstString(dataset.SOPClassUID);
    if (!sopClassUid || !ACCEPTED_SOP_CLASS_UIDS.has(sopClassUid)) {
        return { fileName: file.name, reason: 'unsupported-sop-class' };
    }

    const dicomPatientId = firstString(dataset.PatientID);
    if (!dicomPatientId) {
        return { fileName: file.name, reason: 'missing-patient-id' };
    }

    // Type 1 in the General Study module, and the connector refuses an empty one. Rejected here so
    // a non-conformant file is named during the scan rather than failing the whole upload later.
    const studyInstanceUid = firstString(dataset.StudyInstanceUID);
    if (!studyInstanceUid) {
        return { fileName: file.name, reason: 'missing-study-uid' };
    }

    const seriesInstanceUid = firstString(dataset.SeriesInstanceUID);
    if (!seriesInstanceUid) {
        return { fileName: file.name, reason: 'missing-series-uid' };
    }

    // Type 1 in every composite IOD; without one the file has no identity, so refuse it.
    const sopInstanceUid = firstString(dataset.SOPInstanceUID);
    if (!sopInstanceUid) {
        return { fileName: file.name, reason: 'missing-sop-uid' };
    }

    return {
        file,
        dicomPatientId,
        dicomPatientName: firstString(dataset.PatientName),
        patientSex: firstString(dataset.PatientSex),
        patientBirthDate: firstString(dataset.PatientBirthDate),
        studyInstanceUid,
        seriesInstanceUid,
        sopInstanceUid,
        sopClassUid,
        modality: firstString(dataset.Modality),
        seriesDescription: firstString(dataset.SeriesDescription),
        seriesDateTime: toIsoDateTime(
            firstString(dataset.SeriesDate),
            firstString(dataset.SeriesTime)
        ),
    };
}

function isReject(value: ScannedInstance | ScanReject): value is ScanReject {
    return 'reason' in value;
}

/**
 * Claims the instance's SOPInstanceUID for the whole scan; false means an earlier file already held
 * it. Claimed scan-wide rather than per patient because the UID alone names the object: the
 * connector's uniqueness constraint on stored slices is global, so a second patient claiming a UID
 * would queue as importable and then fail the commit no retry could settle.
 */
function claimSopUid(claimed: Set<string>, instance: ScannedInstance): boolean {
    if (claimed.has(instance.sopInstanceUid)) {
        return false;
    }
    claimed.add(instance.sopInstanceUid);
    return true;
}

/** Scans files sequentially with progress; scan-wide duplicate SOPInstanceUIDs are rejected. */
export async function scanFiles(
    files: File[],
    options: {
        signal?: AbortSignal;
        onProgress?: (scanned: number, total: number) => void;
    } = {}
): Promise<ScanResult> {
    const instances: ScannedInstance[] = [];
    const rejects: ScanReject[] = [];
    const claimedSopUids = new Set<string>();

    for (const [index, file] of files.entries()) {
        if (options.signal?.aborted) {
            break;
        }

        let outcome: ScannedInstance | ScanReject;
        try {
            outcome = scanBuffer(await file.arrayBuffer(), file);
        } catch {
            outcome = { fileName: file.name, reason: 'unreadable' };
        }

        if (isReject(outcome)) {
            rejects.push(outcome);
        } else if (claimSopUid(claimedSopUids, outcome)) {
            instances.push(outcome);
        } else {
            rejects.push({
                fileName: file.name,
                reason: 'duplicate-sop-uid',
            });
        }

        options.onProgress?.(index + 1, files.length);
    }

    return { instances, rejects };
}
